// hooks/useAuthManager.js
import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { logout, refreshSession } from 'store/reducers/userSlice';
import { logoutUser } from 'store/reducers/actions';
import { decodeToken } from 'utils/functions/jwt-token';
import { getJwtToken } from 'utils/functions/session-storage';
import calculateExpirationTime from 'utils/functions/calculate-expiration-time';
import { useUserActivity } from 'hooks/useUserActivity';
import { PUBLIC_PATHS } from 'config';
import { useAuthRedirect } from './useAuthRedirect';

const isPublicPath = (pathname) => {
  const pathComponents = pathname.split('/');
  return PUBLIC_PATHS.includes(pathname) || pathComponents[1] === 'public' || pathComponents[1] === 'register';
};

export const useAuthManager = () => {
  useAuthRedirect(); // Centralized redirect logic

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const logoutTimerRef = useRef(null);
  const hasRefreshedRef = useRef(false);

  // Get token and expiration data
  const token = getJwtToken();
  const tokenData = decodeToken();
  const exp = tokenData?.exp;

  const currentTime = Math.floor(Date.now() / 1000);
  const { expTimeLeft, refreshTimeLeft } = calculateExpirationTime(currentTime, exp);
  //   const [expTimeLeft, refreshTimeLeft] = [120000, 60000];

  // Handle logout
  const handleLogout = useCallback(() => {
    dispatch(logout());
    dispatch(logoutUser());
    if (!isPublicPath(pathname)) {
      navigate('/login', { replace: true });
    }
  }, [dispatch, navigate, pathname]);

  // Handle session refresh
  const handleSessionRefresh = useCallback(() => {
    if (!hasRefreshedRef.current && token) {
      hasRefreshedRef.current = true;
      dispatch(refreshSession());
    }
  }, [dispatch, token]);

  // Use custom hook for user activity detection
  const { isUserActive, resetActivity } = useUserActivity({
    timeout: refreshTimeLeft,
    onActivity: handleSessionRefresh,
    enabled: !!refreshTimeLeft && !!token
  });

  // Handle authentication and routing
  useEffect(() => {
    // console.log(expTimeLeft);
    // Redirect to login logic is now handled by useAuthRedirect

    // Clear existing logout timer
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
    }

    // Set logout timer if token exists and will expire
    if (token && expTimeLeft > 0) {
      logoutTimerRef.current = setTimeout(() => {
        handleLogout();
      }, expTimeLeft);
    }

    // Cleanup function
    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
      }
    };
  }, [token, expTimeLeft, pathname, handleLogout, navigate]);

  // Reset refresh flag when token changes
  useEffect(() => {
    hasRefreshedRef.current = false;
    resetActivity();
  }, [token, resetActivity]);
};
